/**
 * PROTOTYPE UI — throwaway interactive shell for sermon note cards.
 * Three layout variants share this engine; switch via ?variant=
 *
 * Drag uses pointer events (not HTML5 DnD) so the card reliably follows the cursor.
 */

import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type PointerEvent as ReactPointerEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { PresentationChartBarIcon } from '@heroicons/react/24/outline'
import { PresentationChartBarIcon as PresentationChartBarIconSolid } from '@heroicons/react/24/solid'
import type { ProtoCard } from './seed'
import { flattenWithDepth } from './tree'
import type { DropZone, PrototypeState } from './usePrototypeState'

function zoneKey(z: DropZone): string {
	if (z.kind === 'root') return `root:${z.where}`
	return `${z.kind}:${z.where}:${z.targetId}`
}

function sameZone(a: DropZone | null, b: DropZone): boolean {
	return a != null && zoneKey(a) === zoneKey(b)
}

function encodeZone(z: DropZone): string {
	return JSON.stringify(z)
}

function readZoneFromPoint(x: number, y: number): DropZone | null {
	const el = document.elementFromPoint(x, y)
	const hit = el?.closest('[data-drop-zone]') as HTMLElement | null
	if (!hit?.dataset.dropZone) return null
	try {
		return JSON.parse(hit.dataset.dropZone) as DropZone
	} catch {
		return null
	}
}

function previewLabel(zone: DropZone, parentTitle?: string): string {
	if (zone.kind === 'root') {
		return zone.where === 'canvas'
			? 'Drop at end of sermon'
			: 'Drop at end of Someday'
	}
	if (zone.kind === 'into') {
		return parentTitle
			? `Nest under “${parentTitle}”`
			: 'Nest as a sub-card'
	}
	if (zone.kind === 'before') return 'Place above (same level)'
	return 'Place below (same level)'
}

/**
 * Sticky drop target: ignore brief hit-test flicker (scroll / boundary jitter).
 * Never clears immediately on a miss — keeps last zone so the list doesn't thrash.
 */
function createStickyZonePicker(dwellMs = 140) {
	let committed: DropZone | null = null
	let pending: DropZone | null = null
	let pendingAt = 0

	return (hit: DropZone | null): DropZone | null => {
		const now = performance.now()
		if (hit == null) return committed
		if (!committed || zoneKey(hit) === zoneKey(committed)) {
			committed = hit
			pending = null
			return committed
		}
		if (!pending || zoneKey(pending) !== zoneKey(hit)) {
			pending = hit
			pendingAt = now
			return committed
		}
		if (now - pendingAt >= dwellMs) {
			committed = hit
			pending = null
			return committed
		}
		return committed
	}
}

function titleForZone(state: PrototypeState, zone: DropZone): string | undefined {
	if (zone.kind === 'root') return undefined
	const list = zone.where === 'canvas' ? state.canvas : state.someday
	const walk = (cards: ProtoCard[]): string | undefined => {
		for (const c of cards) {
			if (c.id === zone.targetId) return c.faceTitle
			const nested = walk(c.children)
			if (nested) return nested
		}
		return undefined
	}
	return walk(list)
}

/** Card that follows the pointer while dragging. */
function FloatingDragCard({ state }: { state: PrototypeState }) {
	if (typeof document === 'undefined') return null
	if (!state.draggingId || !state.dragCursor) return null
	const card = state.findDraggingCard()
	if (!card) return null
	const zone = state.dropPreview
	const place = zone
		? previewLabel(zone, titleForZone(state, zone))
		: 'Move to a line or row · Esc to cancel'

	return createPortal(
		<div
			className="pointer-events-none fixed z-[100000] w-64 rounded-lg border-2 border-sky-600 bg-white p-2.5 text-zinc-900 shadow-2xl"
			style={{
				left: state.dragCursor.x + 14,
				top: state.dragCursor.y + 14,
				transform: 'rotate(2deg)',
			}}
		>
			<p
				className={`text-[10px] font-bold uppercase tracking-wide ${
					zone?.kind === 'into' ? 'text-violet-700' : 'text-sky-700'
				}`}
			>
				{place}
			</p>
			<p className="mt-0.5 truncate text-sm font-semibold">{card.faceTitle}</p>
		</div>,
		document.body,
	)
}

function Menu({
	label,
	items,
}: {
	label: string
	items: Array<{ label: string; onClick: () => void }>
}) {
	const [open, setOpen] = useState(false)
	return (
		<div className="relative">
			<button
				type="button"
				className="rounded px-1.5 py-0.5 text-xs font-medium text-zinc-700 hover:bg-zinc-200"
				onClick={() => setOpen((o) => !o)}
			>
				{label}
			</button>
			{open ? (
				<div className="absolute right-0 z-30 mt-1 min-w-[11rem] rounded border border-zinc-300 bg-white py-1 text-zinc-900 shadow-lg">
					{items.map((item) => (
						<button
							key={item.label}
							type="button"
							className="block w-full px-3 py-1.5 text-left text-xs text-zinc-900 hover:bg-zinc-100"
							onClick={() => {
								item.onClick()
								setOpen(false)
							}}
						>
							{item.label}
						</button>
					))}
				</div>
			) : null}
		</div>
	)
}

function DropLine({
	zone,
	state,
	indentPx = 0,
	compact = false,
}: {
	zone: DropZone
	state: PrototypeState
	indentPx?: number
	compact?: boolean
}) {
	const active = sameZone(state.dropPreview, zone)
	const insertHover =
		!state.draggingId &&
		zone.where === 'canvas' &&
		state.mode === 'cards' &&
		state.listMode === 'cards' &&
		sameZone(state.hoverInsert, zone)

	const onEnter = () => {
		if (
			state.draggingId ||
			zone.where !== 'canvas' ||
			state.mode !== 'cards' ||
			state.listMode !== 'cards'
		) {
			return
		}
		state.setHoverInsert(zone)
	}
	const onLeave = () => {
		if (sameZone(state.hoverInsert, zone)) {
			state.setHoverInsert(null)
		}
	}

	// Fixed hit height — never insert a ghost card (that reflows and thrash-oscillates).
	if (!compact && !state.draggingId) {
		return (
			<div
				data-drop-zone={encodeZone(zone)}
				className="relative flex h-3 items-center"
				style={{ marginLeft: indentPx }}
				onMouseEnter={onEnter}
				onMouseLeave={onLeave}
			>
				<div
					className={`w-full rounded transition-colors duration-150 ${
						insertHover ? 'h-1 bg-sky-500' : 'h-0.5 bg-transparent'
					}`}
				/>
				{insertHover ? (
					<span className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded bg-sky-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow">
						N
					</span>
				) : null}
			</div>
		)
	}
	return (
		<div
			data-drop-zone={encodeZone(zone)}
			className={
				compact
					? 'relative flex h-3 items-center'
					: 'relative flex h-4 items-center'
			}
			style={{ marginLeft: indentPx }}
			onMouseEnter={onEnter}
			onMouseLeave={onLeave}
		>
			<div
				className={`w-full rounded transition-colors duration-100 ${
					active
						? zone.kind === 'into'
							? 'h-1.5 bg-violet-500'
							: 'h-1.5 bg-sky-500'
						: insertHover
							? 'h-1 bg-sky-500'
							: compact
								? 'h-0.5 bg-zinc-300'
								: 'h-1 bg-zinc-300/80'
				}`}
			/>
			{insertHover ? (
				<span className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded bg-sky-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow">
					N
				</span>
			) : null}
		</div>
	)
}

const OUTLINE_INDENT = 28

/** Compact stack-of-buttons tree for precise drop targeting while dragging. */
function CompactOutlineNode({
	card,
	depth,
	where,
	state,
}: {
	card: ProtoCard
	depth: number
	where: 'canvas' | 'someday'
	state: PrototypeState
}) {
	const indentPx = (depth - 1) * OUTLINE_INDENT
	const nestZone: DropZone = { kind: 'into', where, targetId: card.id }
	const nestActive = sameZone(state.dropPreview, nestZone)
	const isSource = state.draggingId === card.id
	const kids = card.children

	return (
		<div className="transition-all duration-300 ease-out">
			<div
				data-drop-zone={
					isSource ? undefined : encodeZone(nestZone)
				}
				className={`flex h-9 items-center gap-2 rounded-md border px-2 text-zinc-900 shadow-sm transition-all duration-300 ${
					isSource
						? 'border-dashed border-zinc-400 bg-zinc-100 opacity-40'
						: nestActive
							? 'border-violet-500 bg-violet-100 ring-2 ring-violet-300'
							: 'border-zinc-300 bg-white hover:border-sky-400'
				}`}
				style={{ marginLeft: indentPx }}
			>
				<span className="w-6 shrink-0 text-[10px] font-bold text-zinc-500">
					L{depth}
				</span>
				<span className="min-w-0 flex-1 truncate text-sm font-medium">
					{card.faceTitle || 'Untitled'}
				</span>
				{kids.length > 0 ? (
					<span className="shrink-0 text-[10px] font-medium text-zinc-500">
						{kids.length}
					</span>
				) : null}
				{where === 'canvas' && card.presentationTagged ? (
					<PresentationChartBarIcon className="h-3.5 w-3.5 shrink-0 text-violet-700" />
				) : null}
			</div>
			{kids.map((ch, i) => (
				<div key={ch.id}>
					<DropLine
						state={state}
						compact
						indentPx={depth * OUTLINE_INDENT}
						zone={{ kind: 'before', where, targetId: ch.id }}
					/>
					<CompactOutlineNode
						card={ch}
						depth={depth + 1}
						where={where}
						state={state}
					/>
					{i === kids.length - 1 ? (
						<DropLine
							state={state}
							compact
							indentPx={depth * OUTLINE_INDENT}
							zone={{ kind: 'after', where, targetId: ch.id }}
						/>
					) : null}
				</div>
			))}
		</div>
	)
}

function CompactOutlineStack({
	cards,
	where,
	state,
}: {
	cards: ProtoCard[]
	where: 'canvas' | 'someday'
	state: PrototypeState
}) {
	return (
		<div className="flex flex-col gap-0.5 rounded-lg border border-sky-200 bg-sky-50/60 p-2 transition-all duration-300 ease-out">
			<p className="mb-1 px-1 text-[10px] font-bold uppercase tracking-wide text-sky-900">
				Outline placement — drop on a row to nest, or on a line to place beside
			</p>
			<DropLine
				state={state}
				compact
				zone={{ kind: 'root', where }}
			/>
			{cards.map((c, i) => (
				<div key={c.id} className="transition-all duration-300">
					{i === 0 ? null : (
						<DropLine
							state={state}
							compact
							zone={{ kind: 'before', where, targetId: c.id }}
						/>
					)}
					<CompactOutlineNode
						card={c}
						depth={1}
						where={where}
						state={state}
					/>
					{i === cards.length - 1 ? (
						<DropLine
							state={state}
							compact
							zone={{ kind: 'after', where, targetId: c.id }}
						/>
					) : null}
				</div>
			))}
			{cards.length === 0 ? (
				<p className="py-4 text-center text-xs font-medium text-zinc-600">
					Drop here
				</p>
			) : null}
		</div>
	)
}

function CardNode({
	card,
	depth,
	where,
	state,
}: {
	card: ProtoCard
	depth: number
	where: 'canvas' | 'someday'
	state: PrototypeState
}) {
	const open = state.openBackId === card.id
	const selected = state.selectedId === card.id
	const collapsed = state.isCollapsed(card.id)
	const hasKids = card.children.length > 0
	const showKids =
		hasKids &&
		!collapsed &&
		(where === 'someday' ||
			(where === 'canvas' && state.orientation === 'vertical'))
	const nestZone: DropZone = { kind: 'into', where, targetId: card.id }
	const nestActive = sameZone(state.dropPreview, nestZone)
	const stateRef = useRef(state)
	stateRef.current = state
	const titleRef = useRef<HTMLInputElement>(null)
	const justInserted = state.justInsertedId === card.id

	useEffect(() => {
		if (state.focusTitleId !== card.id) return
		const el = titleRef.current
		if (!el) return
		el.scrollIntoView({ behavior: 'smooth', block: 'center' })
		// After scroll starts, focus — rAF helps when the card just mounted.
		requestAnimationFrame(() => {
			el.focus()
			el.select()
			stateRef.current.clearFocusTitle()
		})
	}, [state.focusTitleId, card.id])

	const onHandlePointerDown = (e: ReactPointerEvent) => {
		e.preventDefault()
		e.stopPropagation()
		;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)

		const start = {
			id: card.id,
			where,
			x: e.clientX,
			y: e.clientY,
			active: false,
			cancelled: false,
		}
		const pickZone = createStickyZonePicker(140)
		const prevUserSelect = document.body.style.userSelect
		let lastPreviewKey = ''

		const cleanup = () => {
			document.body.style.userSelect = prevUserSelect
			document.body.style.cursor = ''
			window.removeEventListener('pointermove', onMove)
			window.removeEventListener('pointerup', onUp)
			window.removeEventListener('pointercancel', onUp)
			window.removeEventListener('keydown', onKey)
		}

		const onKey = (ev: KeyboardEvent) => {
			if (ev.key !== 'Escape') return
			ev.preventDefault()
			start.cancelled = true
			cleanup()
			if (start.active) {
				stateRef.current.clearDrag()
			}
		}

		const onMove = (ev: PointerEvent) => {
			if (start.cancelled) return
			const s = stateRef.current
			if (!start.active) {
				if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < 4) return
				start.active = true
				document.body.style.userSelect = 'none'
				document.body.style.cursor = 'grabbing'
				s.beginDrag(start.id, start.where)
			}
			s.setDragCursor({ x: ev.clientX, y: ev.clientY })
			const zone = pickZone(readZoneFromPoint(ev.clientX, ev.clientY))
			const key = zone ? zoneKey(zone) : ''
			if (key !== lastPreviewKey) {
				lastPreviewKey = key
				s.setDropPreview(zone)
			}
		}

		const onUp = (ev: PointerEvent) => {
			if (start.cancelled) return
			const s = stateRef.current
			cleanup()
			if (!start.active) return
			const zone =
				pickZone(readZoneFromPoint(ev.clientX, ev.clientY)) ??
				s.dropPreview
			if (zone) {
				s.handleDrop(start.id, start.where, zone)
			} else {
				s.clearDrag()
			}
		}

		window.addEventListener('pointermove', onMove)
		window.addEventListener('pointerup', onUp)
		window.addEventListener('pointercancel', onUp)
		window.addEventListener('keydown', onKey)
	}

	const faceChrome = selected
		? 'border-sky-500 ring-1 ring-sky-400'
		: nestActive
			? 'border-violet-500 ring-2 ring-violet-400'
			: 'border-zinc-300'

	return (
		<div
			className={`${depth > 1 ? 'mt-2' : ''} transition-[margin,transform,opacity] duration-300 ease-out ${
				justInserted
					? 'animate-in fade-in slide-in-from-top-3 zoom-in-95 duration-300'
					: ''
			} ${state.draggingId === card.id ? 'pointer-events-none invisible' : ''}`}
		>
			<div
				data-drop-zone={
					state.draggingId && state.draggingId !== card.id
						? encodeZone(nestZone)
						: undefined
				}
			>
				{/* 3D flip: face ↔ back (flip up via rotateX) */}
				<div className="w-full [perspective:1400px]">
					<div
						className="grid w-full transition-transform duration-500 ease-[cubic-bezier(0.4,0.15,0.2,1)]"
						style={{
							gridTemplateAreas: '"stack"',
							transformStyle: 'preserve-3d',
							transform: open ? 'rotateX(180deg)' : 'rotateX(0deg)',
						}}
					>
						{/* FACE */}
						<div
							className={`rounded-lg border bg-white text-zinc-900 ${faceChrome} ${
								justInserted ? 'ring-2 ring-sky-400' : ''
							}`}
							style={{
								gridArea: 'stack',
								backfaceVisibility: 'hidden',
								WebkitBackfaceVisibility: 'hidden',
							}}
						>
							<div
								className="flex cursor-pointer items-stretch gap-2 p-3"
								onClick={() => state.setSelectedId(card.id)}
							>
								<div className="flex w-7 shrink-0 flex-col items-center justify-between gap-1 self-stretch py-0.5">
									{hasKids ? (
										<button
											type="button"
											className="rounded border border-zinc-300 bg-zinc-100 px-1.5 text-xs font-bold text-zinc-800"
											onClick={(e) => {
												e.stopPropagation()
												state.toggleCollapsed(card.id)
											}}
											title={collapsed ? 'Expand' : 'Collapse'}
										>
											{collapsed ? '▸' : '▾'}
										</button>
									) : (
										<span className="h-5 w-5" aria-hidden />
									)}
									<div
										role="button"
										tabIndex={0}
										onPointerDown={onHandlePointerDown}
										className="cursor-grab select-none touch-none rounded border border-zinc-300 bg-zinc-100 px-1.5 py-0.5 text-xs font-bold text-zinc-700 active:cursor-grabbing"
										title="Drag to reorder / nest / move columns"
										onClick={(e) => e.stopPropagation()}
										onKeyDown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') e.preventDefault()
										}}
									>
										⋮⋮
									</div>
								</div>
								<button
									type="button"
									className="mt-0.5 shrink-0 self-start rounded border border-zinc-300 bg-zinc-100 px-1.5 text-xs font-medium text-zinc-800"
									onClick={(e) => {
										e.stopPropagation()
										state.setOpenBackId(card.id)
									}}
									title="Flip card to show the back"
								>
									↻
								</button>
								<div className="min-w-0 flex-1">
									<div className="flex flex-wrap items-center gap-2">
										<span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
											L{depth}
											{hasKids && collapsed
												? ` · ${card.children.length}`
												: ''}
										</span>
										{where === 'canvas' && card.presentationTagged ? (
											<span className="rounded bg-violet-200 px-1.5 py-0.5 text-[10px] font-semibold text-violet-900">
												presentation
											</span>
										) : null}
										{card.labels.map((l) => (
											<span
												key={l}
												className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium text-amber-950"
											>
												{l}
											</span>
										))}
									</div>
									<input
										ref={titleRef}
										className="mt-1 w-full border-0 bg-transparent text-sm font-semibold text-zinc-900 outline-none placeholder:text-zinc-400"
										value={card.faceTitle}
										placeholder="Card title"
										onChange={(e) =>
											state.updateCard(card.id, where, {
												faceTitle: e.target.value,
											})
										}
										onClick={(e) => e.stopPropagation()}
									/>
									<input
										className="w-full border-0 bg-transparent text-xs text-zinc-700 outline-none placeholder:text-zinc-400"
										value={card.faceSummary}
										onChange={(e) =>
											state.updateCard(card.id, where, {
												faceSummary: e.target.value,
											})
										}
										onClick={(e) => e.stopPropagation()}
										placeholder="Face summary"
									/>
									{where === 'someday' ? (
										<p className="mt-1 text-[10px] font-medium text-zinc-600">
											{card.authorName}
										</p>
									) : null}
								</div>
								<div
									className="flex shrink-0 flex-col items-end gap-1"
									onClick={(e) => e.stopPropagation()}
								>
									{where === 'canvas' ? (
										<button
											type="button"
											className={`rounded p-1 ${card.presentationTagged ? 'bg-violet-700 text-white' : 'border border-zinc-300 bg-zinc-100 text-zinc-700'}`}
											onClick={() =>
												state.togglePresentation(card.id, where)
											}
											title={
												card.presentationTagged
													? 'Tagged for presentation'
													: 'Tag for presentation'
											}
											aria-label={
												card.presentationTagged
													? 'Tagged for presentation'
													: 'Tag for presentation'
											}
											aria-pressed={card.presentationTagged}
										>
											{card.presentationTagged ? (
												<PresentationChartBarIconSolid className="h-4 w-4" />
											) : (
												<PresentationChartBarIcon className="h-4 w-4" />
											)}
										</button>
									) : null}
									{where === 'canvas' ? (
										<Menu
											label="•••"
											items={[
												{
													label: 'Copy to Someday',
													onClick: () => state.copyToSomeday(card.id),
												},
												{
													label: 'Move to Someday',
													onClick: () => state.moveToSomeday(card.id),
												},
												{
													label: 'Move up',
													onClick: () => state.moveSibling(card.id, -1),
												},
												{
													label: 'Move down',
													onClick: () => state.moveSibling(card.id, 1),
												},
												{
													label: 'Indent (nest)',
													onClick: () => state.indent(card.id),
												},
												{
													label: 'Outdent',
													onClick: () => state.outdent(card.id),
												},
											]}
										/>
									) : (
										<Menu
											label="•••"
											items={[
												{
													label: 'Copy to this sermon',
													onClick: () => state.copyToCanvas(card.id),
												},
												{
													label: 'Move to this sermon',
													onClick: () => state.moveToCanvas(card.id),
												},
												{
													label: 'Copy to new sermon',
													onClick: () => state.startNewSermon(card.id, true),
												},
												{
													label: 'Start new sermon',
													onClick: () => state.startNewSermon(card.id, false),
												},
											]}
										/>
									)}
								</div>
							</div>
						</div>

						{/* BACK — pre-rotated so the parent flip reveals it */}
						<div
							className="rounded-lg border border-amber-300 bg-[#fff8ef] text-zinc-900 shadow-inner"
							style={{
								gridArea: 'stack',
								backfaceVisibility: 'hidden',
								WebkitBackfaceVisibility: 'hidden',
								transform: 'rotateX(180deg)',
							}}
						>
							<div className="flex h-full min-h-[9rem] flex-col p-3">
								<div className="mb-2 flex items-center justify-between gap-2">
									<div>
										<p className="text-[10px] font-bold uppercase tracking-wide text-amber-800">
											Card back
										</p>
										<p className="text-xs font-semibold text-zinc-800">
											{card.faceTitle || 'Untitled'}
										</p>
									</div>
									<button
										type="button"
										className="rounded border border-amber-400 bg-white px-2 py-1 text-xs font-medium text-zinc-900"
										onClick={(e) => {
											e.stopPropagation()
											state.setOpenBackId(null)
										}}
										title="Flip back to face"
									>
										Flip over
									</button>
								</div>
								<textarea
									className="min-h-[6rem] w-full flex-1 resize-y rounded border border-amber-200 bg-white p-2 text-sm text-zinc-900 outline-none"
									value={card.back}
									onChange={(e) =>
										state.updateCard(card.id, where, { back: e.target.value })
									}
									placeholder="Full speaking text…"
									onClick={(e) => e.stopPropagation()}
								/>
							</div>
						</div>
					</div>
				</div>

				{showKids ? (
					<div className="mt-1 space-y-0 rounded-lg border border-zinc-200 bg-zinc-50/80 px-2 pb-2 pt-1">
						{card.children.map((ch, i) => (
							<div key={ch.id}>
								<DropLine
									state={state}
									zone={{ kind: 'before', where, targetId: ch.id }}
								/>
								<CardNode
									card={ch}
									depth={depth + 1}
									where={where}
									state={state}
								/>
								{i === card.children.length - 1 ? (
									<DropLine
										state={state}
										zone={{ kind: 'after', where, targetId: ch.id }}
									/>
								) : null}
							</div>
						))}
					</div>
				) : null}
			</div>
		</div>
	)
}

function CardStack({
	cards,
	where,
	state,
}: {
	cards: ProtoCard[]
	where: 'canvas' | 'someday'
	state: PrototypeState
}) {
	const outline = state.listMode === 'outline'

	if (outline) {
		return (
			<div
				key="outline"
				className="animate-in fade-in zoom-in-95 duration-300"
			>
				<CompactOutlineStack cards={cards} where={where} state={state} />
			</div>
		)
	}

	if (where === 'canvas' && state.orientation === 'horizontal') {
		const flat = flattenWithDepth(cards)
		return (
			<div
				key="cards-h"
				className="flex gap-3 overflow-x-auto pb-2 animate-in fade-in duration-300"
			>
				{flat.map(({ card, depth }) => (
					<div key={card.id} className="w-72 shrink-0">
						<CardNode card={card} depth={depth} where={where} state={state} />
					</div>
				))}
			</div>
		)
	}

	return (
		<div
			key="cards-v"
			className="flex flex-col animate-in fade-in slide-in-from-top-2 duration-300"
		>
			{cards.length === 0 ? (
				<>
					<DropLine state={state} zone={{ kind: 'root', where }} />
					<p className="py-6 text-center text-xs font-medium text-zinc-600">
						{where === 'canvas'
							? 'No cards yet — use + New card, or hover a gap and press N'
							: 'Drop cards here'}
					</p>
				</>
			) : (
				<>
					<DropLine
						state={state}
						zone={{ kind: 'before', where, targetId: cards[0]!.id }}
					/>
					{cards.map((c, i) => (
						<div
							key={c.id}
							className="transition-[margin,transform] duration-300 ease-out"
						>
							{i === 0 ? null : (
								<DropLine
									state={state}
									zone={{ kind: 'before', where, targetId: c.id }}
								/>
							)}
							<CardNode card={c} depth={1} where={where} state={state} />
							{i === cards.length - 1 ? (
								<DropLine
									state={state}
									zone={{ kind: 'after', where, targetId: c.id }}
								/>
							) : null}
						</div>
					))}
				</>
			)}
		</div>
	)
}

function SomedayPanel({
	state,
	className,
}: {
	state: PrototypeState
	className?: string
}) {
	const suggestions = useMemo(() => {
		const q = state.searchText.trim().toLowerCase()
		if (!q)
			return [] as Array<{ kind: 'label' | 'author'; value: string; label: string }>
		const out: Array<{ kind: 'label' | 'author'; value: string; label: string }> =
			[]
		for (const l of state.labelOptions) {
			if (l.toLowerCase().includes(q) && !state.labelPills.includes(l)) {
				out.push({ kind: 'label', value: l, label: `Label: ${l}` })
			}
		}
		for (const a of state.authorOptions) {
			if (
				a.name.toLowerCase().includes(q) &&
				!state.authorPills.includes(a.id)
			) {
				out.push({ kind: 'author', value: a.id, label: `Author: ${a.name}` })
			}
		}
		return out.slice(0, 8)
	}, [
		state.searchText,
		state.labelOptions,
		state.authorOptions,
		state.labelPills,
		state.authorPills,
	])

	const somedayRoot: DropZone = { kind: 'root', where: 'someday' }
	const rootActive = sameZone(state.dropPreview, somedayRoot)

	return (
		<aside
			data-drop-zone={encodeZone(somedayRoot)}
			className={`box-border flex h-full min-h-0 w-[22rem] min-w-[22rem] max-w-[22rem] shrink-0 flex-col overflow-hidden border-zinc-300 bg-zinc-100 text-zinc-900 ${className ?? ''} ${rootActive ? 'ring-2 ring-inset ring-sky-400' : ''}`}
		>
			<div className="grid w-full min-w-0 shrink-0 grid-cols-1 gap-2 border-b border-zinc-300 bg-zinc-100 p-3">
				<div className="min-w-0">
					<h2 className="text-sm font-semibold tracking-tight text-zinc-900">
						Someday
					</h2>
					<p className="text-[11px] font-medium text-zinc-600">
						Ministry bucket · search + label/author pills
					</p>
				</div>
				{/* Single-column grid keeps input, suggestions, and pills the same width */}
				<input
					className="box-border block w-full min-w-0 rounded border border-zinc-400 bg-white px-2 py-1.5 text-xs text-zinc-900 outline-none placeholder:text-zinc-500"
					style={{ width: '100%', maxWidth: '100%' }}
					placeholder="Search or pick label/author…"
					value={state.searchText}
					onChange={(e) => state.setSearchText(e.target.value)}
				/>
				{suggestions.length ? (
					<ul
						className="box-border m-0 max-h-48 w-full min-w-0 list-none overflow-auto overflow-x-hidden rounded border border-zinc-400 bg-white p-0 text-zinc-900"
						style={{ width: '100%', maxWidth: '100%' }}
					>
						{suggestions.map((s) => (
							<li key={`${s.kind}-${s.value}`} className="min-w-0 border-b border-zinc-100 last:border-b-0">
								<button
									type="button"
									className="box-border block w-full min-w-0 truncate px-2 py-1.5 text-left text-xs text-zinc-900 hover:bg-zinc-100"
									style={{ width: '100%', maxWidth: '100%' }}
									onClick={() => {
										if (s.kind === 'label') {
											state.setLabelPills((p) => [...p, s.value])
										} else {
											state.setAuthorPills((p) => [...p, s.value])
										}
										state.setSearchText('')
									}}
								>
									{s.label}
								</button>
							</li>
						))}
					</ul>
				) : null}
				{(state.labelPills.length || state.authorPills.length) > 0 ? (
					<div className="flex w-full min-w-0 flex-wrap gap-1">
						{state.labelPills.map((l) => (
							<button
								key={l}
								type="button"
								className="max-w-full truncate rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-950"
								onClick={() =>
									state.setLabelPills((p) => p.filter((x) => x !== l))
								}
							>
								{l} ×
							</button>
						))}
						{state.authorPills.map((id) => {
							const name =
								state.authorOptions.find((a) => a.id === id)?.name ?? id
							return (
								<button
									key={id}
									type="button"
									className="max-w-full truncate rounded-full bg-sky-200 px-2 py-0.5 text-[10px] font-medium text-sky-950"
									onClick={() =>
										state.setAuthorPills((p) => p.filter((x) => x !== id))
									}
								>
									{name} ×
								</button>
							)
						})}
					</div>
				) : null}
			</div>
			<div className="min-h-0 w-full min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-3">
				<CardStack cards={state.someday} where="someday" state={state} />
			</div>
		</aside>
	)
}

function Toolbar({ state }: { state: PrototypeState }) {
	const segIdle =
		'rounded-md px-2.5 py-1 text-zinc-800 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40'
	const segOn = 'rounded-md bg-zinc-900 px-2.5 py-1 text-white'

	return (
		<header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-zinc-300 bg-white px-4 py-3 text-zinc-900">
			<div>
				<p className="text-[10px] font-bold uppercase tracking-wider text-amber-800">
					PROTOTYPE — throwaway
				</p>
				<h1 className="text-lg font-semibold text-zinc-900">I Have a Dream</h1>
			</div>
			<div className="ml-auto flex flex-wrap items-center gap-2">
				<div className="inline-flex rounded-lg border border-zinc-400 bg-white p-0.5 text-xs">
					<button
						type="button"
						className={state.mode === 'document' ? segOn : segIdle}
						onClick={() => state.trySetMode('document')}
					>
						Document
					</button>
					<button
						type="button"
						disabled={state.cardsDisabled}
						className={state.mode === 'cards' ? segOn : segIdle}
						onClick={() => state.trySetMode('cards')}
						title={
							state.cardsDisabled
								? 'Clear the document to enable Cards'
								: undefined
						}
					>
						Cards
					</button>
				</div>
				{state.mode === 'cards' ? (
					<>
						<button
							type="button"
							className="rounded-md bg-sky-700 px-2.5 py-1 text-xs font-semibold text-white shadow-sm hover:bg-sky-800"
							onClick={() => state.addCardAtEnd()}
							title="Add a new card at the end of the sermon"
						>
							+ New card
						</button>
						<div className="inline-flex rounded-lg border border-zinc-400 bg-white p-0.5 text-xs">
							<button
								type="button"
								className={
									state.orientation === 'vertical' ? segOn : segIdle
								}
								onClick={() => state.setOrientation('vertical')}
							>
								Vertical
							</button>
							<button
								type="button"
								className={
									state.orientation === 'horizontal' ? segOn : segIdle
								}
								onClick={() => state.setOrientation('horizontal')}
							>
								Horizontal
							</button>
						</div>
						<button
							type="button"
							className="rounded border border-zinc-400 bg-white px-2 py-1 text-xs font-medium text-zinc-900"
							onClick={() => state.setModal('export')}
						>
							Export…
						</button>
						<button
							type="button"
							className="rounded border border-rose-400 bg-white px-2 py-1 text-xs font-medium text-rose-800"
							onClick={() => state.setModal('convert')}
						>
							Convert to document
						</button>
					</>
				) : null}
			</div>
		</header>
	)
}

function Modals({ state }: { state: PrototypeState }) {
	if (!state.modal) return null
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
			<div className="w-full max-w-md rounded-xl border border-zinc-300 bg-white p-5 text-zinc-900 shadow-xl">
				{state.modal === 'convert' ? (
					<>
						<h3 className="font-semibold text-zinc-900">Convert to document?</h3>
						<p className="mt-2 text-sm text-zinc-700">
							This can’t be undone. Your cards become a single document; the
							card deck is removed. Presentation tags are dropped; images would
							come along in production.
						</p>
						<div className="mt-4 flex justify-end gap-2">
							<button
								type="button"
								className="rounded px-3 py-1.5 text-sm font-medium text-zinc-800"
								onClick={() => state.setModal(null)}
							>
								Cancel
							</button>
							<button
								type="button"
								className="rounded bg-rose-600 px-3 py-1.5 text-sm font-medium text-white"
								onClick={state.confirmConvert}
							>
								Convert
							</button>
						</div>
					</>
				) : (
					<>
						<h3 className="font-semibold text-zinc-900">Export document</h3>
						<p className="mt-2 text-sm text-zinc-700">
							Stub modal — would download RTF/PDF. Hierarchy: H1–H3 by depth;
							face then back.
						</p>
						<label className="mt-3 flex items-center gap-2 text-sm text-zinc-900">
							<input
								type="checkbox"
								checked={state.exportTaggedOnly}
								onChange={(e) => state.setExportTaggedOnly(e.target.checked)}
							/>
							Presentation-tagged cards only
						</label>
						<div className="mt-3 flex gap-3 text-sm text-zinc-900">
							<label className="flex items-center gap-1">
								<input
									type="radio"
									checked={state.exportFormat === 'pdf'}
									onChange={() => state.setExportFormat('pdf')}
								/>
								PDF
							</label>
							<label className="flex items-center gap-1">
								<input
									type="radio"
									checked={state.exportFormat === 'rtf'}
									onChange={() => state.setExportFormat('rtf')}
								/>
								RTF
							</label>
						</div>
						<div className="mt-4 flex justify-end gap-2">
							<button
								type="button"
								className="rounded px-3 py-1.5 text-sm font-medium text-zinc-800"
								onClick={() => state.setModal(null)}
							>
								Cancel
							</button>
							<button
								type="button"
								className="rounded bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white"
								onClick={() => {
									state.flash(
										`Stub download: ${state.exportFormat.toUpperCase()}${state.exportTaggedOnly ? ' (tagged only)' : ''}`,
									)
									state.setModal(null)
								}}
							>
								Download (stub)
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	)
}

function DebugBar({ state }: { state: PrototypeState }) {
	return (
		<pre className="max-h-20 shrink-0 overflow-auto border-t border-dashed border-amber-400 bg-amber-100 px-3 py-2 text-[10px] text-amber-950">
			{JSON.stringify(state.debugState, null, 0)}
		</pre>
	)
}

const SOMEDAY_WIDTH =
	'w-[22rem] min-w-[22rem] max-w-[22rem] shrink-0'

export function SermonNoteCardsShell({
	state,
	somedayPlacement,
}: {
	state: PrototypeState
	somedayPlacement: 'right' | 'left' | 'drawer'
}) {
	const [drawerOpen, setDrawerOpen] = useState(true)
	const stateRef = useRef(state)
	stateRef.current = state

	const canvasRoot: DropZone = { kind: 'root', where: 'canvas' }

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== 'n' && e.key !== 'N') return
			if (e.metaKey || e.ctrlKey || e.altKey) return
			const t = e.target as HTMLElement | null
			if (
				t &&
				(t.tagName === 'INPUT' ||
					t.tagName === 'TEXTAREA' ||
					t.tagName === 'SELECT' ||
					t.isContentEditable)
			) {
				return
			}
			const s = stateRef.current
			if (s.mode !== 'cards' || s.draggingId || s.listMode !== 'cards') return
			const slot = s.hoverInsert
			if (!slot || slot.where !== 'canvas') return
			e.preventDefault()
			s.insertCardAt(slot)
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [])

	return (
		<div
			className="flex h-dvh max-h-dvh flex-col overflow-hidden bg-zinc-200 text-zinc-900"
			style={{ colorScheme: 'light' }}
		>
			<FloatingDragCard state={state} />
			<Toolbar state={state} />
			{/* Fixed overlays — keep out of document flow so drop doesn't jump the columns */}
			{state.toast ? (
				<div className="pointer-events-none fixed left-1/2 top-3 z-[100001] -translate-x-1/2 rounded-full bg-zinc-900 px-4 py-2 text-xs font-medium text-white shadow-lg">
					{state.toast}
				</div>
			) : null}
			{state.listMode === 'outline' ? (
				<div className="pointer-events-none fixed left-1/2 top-3 z-[100001] -translate-x-1/2 rounded-full border border-sky-400 bg-sky-100 px-4 py-2 text-xs font-medium text-sky-950 shadow-lg">
					{state.draggingId
						? 'Outline mode — Esc cancels'
						: 'Expanding cards…'}
				</div>
			) : null}

			{state.mode === 'document' ? (
				<div className="min-h-0 flex-1 overflow-y-auto p-4">
					<textarea
						className="min-h-[24rem] w-full rounded-lg border border-zinc-400 bg-white p-4 font-mono text-sm text-zinc-900 outline-none"
						value={state.docText}
						onChange={(e) => state.setDocText(e.target.value)}
						placeholder="TipTap stub — type here. Cards stays disabled until this is cleared."
					/>
				</div>
			) : somedayPlacement === 'drawer' ? (
				<div className="relative flex min-h-0 flex-1 overflow-hidden">
					<main
						data-drop-zone={encodeZone(canvasRoot)}
						className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4"
					>
						<div className="mb-2 flex justify-end">
							<button
								type="button"
								className="rounded border border-zinc-400 bg-white px-2 py-1 text-xs font-medium text-zinc-900"
								onClick={() => setDrawerOpen((o) => !o)}
							>
								{drawerOpen ? 'Hide Someday' : 'Show Someday'}
							</button>
						</div>
						<CardStack cards={state.canvas} where="canvas" state={state} />
					</main>
					{drawerOpen ? (
						<div className={`absolute bottom-0 right-0 top-0 ${SOMEDAY_WIDTH} border-l border-zinc-300 shadow-xl`}>
							<SomedayPanel state={state} className="border-0" />
						</div>
					) : null}
				</div>
			) : somedayPlacement === 'left' ? (
				<div className="flex min-h-0 flex-1 overflow-hidden">
					<SomedayPanel state={state} className="border-r" />
					<main
						data-drop-zone={encodeZone(canvasRoot)}
						className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4"
					>
						<CardStack cards={state.canvas} where="canvas" state={state} />
					</main>
				</div>
			) : (
				<div className="flex min-h-0 flex-1 overflow-hidden">
					<main
						data-drop-zone={encodeZone(canvasRoot)}
						className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4"
					>
						<CardStack cards={state.canvas} where="canvas" state={state} />
					</main>
					<SomedayPanel state={state} className="border-l" />
				</div>
			)}

			<Modals state={state} />
			<DebugBar state={state} />
		</div>
	)
}

export function VariantA({ state }: { state: PrototypeState }) {
	return <SermonNoteCardsShell state={state} somedayPlacement="right" />
}
VariantA.displayName = 'Someday right rail'

export function VariantB({ state }: { state: PrototypeState }) {
	return <SermonNoteCardsShell state={state} somedayPlacement="left" />
}
VariantB.displayName = 'Someday left library'

export function VariantC({ state }: { state: PrototypeState }) {
	return <SermonNoteCardsShell state={state} somedayPlacement="drawer" />
}
VariantC.displayName = 'Someday drawer'
